import React, { useState, useEffect } from "react";

import { TextField } from "@aclymatepackages/atoms";
import { editObjectData } from "@aclymatepackages/array-immutability-helpers";

import {
  buildInitialTransactionInputValueFromSchema,
  isEmissionsInputSchemaEnabled,
} from "../../../helpers/components/inputs";

import {
  fetchOurApi,
  fetchAddressEGrid,
} from "../../../helpers/utils/apiCalls";
import { useAccountData } from "../../../helpers/firebase";
import { useOfficesWithCurrentChanges } from "../../../helpers/components/offices";

const ChillWaterInput = ({ chillWaterKwh, editChillWaterData, disabled }) => {
  return (
    <TextField
      label="Chill Water Kwh Used"
      value={chillWaterKwh}
      setValue={editChillWaterData("chillWaterKwh")}
      disabled={disabled}
    />
  );
};

const useChillWaterInput = ({ transaction, onSave }) => {
  const { office, status } = transaction;

  const [offices] = useOfficesWithCurrentChanges();
  const [{ address }, accountDataLoading] = useAccountData();
  const { eGrid } = address || {};

  const inputSchema = [{ field: "chillWaterKwh" }];

  const initialTransactionInputValue =
    buildInitialTransactionInputValueFromSchema(transaction, inputSchema);

  const [tonsCo2ePerMwh, setTonsCo2ePerMwh] = useState(0);
  const [chillWaterData, setChillWaterData] = useState(
    initialTransactionInputValue
  );

  useEffect(() => {
    const findEgridTonsPerMwh = async () => {
      const fetchAndSetEgridTonsPerMwh = async (eGrid) =>
        await fetchOurApi({
          path: "/calcs/utilities/egrid-tons-per-mwh",
          method: "POST",
          data: { eGrid },
          callback: ({ tonsPerMwh }) => setTonsCo2ePerMwh(tonsPerMwh),
        });

      if (!office) {
        if (eGrid) {
          return await fetchAndSetEgridTonsPerMwh(eGrid);
        }
        return;
      }

      if (office) {
        const { currentUtilities, address } =
          offices.find(({ id }) => id === office.id) || {};
        const { eGrid: officeEgrid } = currentUtilities || {};

        const resolvedEGrid = officeEgrid || (await fetchAddressEGrid(address));
        if (resolvedEGrid) {
          return await fetchAndSetEgridTonsPerMwh(resolvedEGrid);
        }
        return;
      }
    };

    if (!accountDataLoading) {
      findEgridTonsPerMwh();
    }
  }, [eGrid, accountDataLoading, office, offices]);

  const { chillWaterKwh } = chillWaterData || {};

  const editChillWaterData = (field) => (value) =>
    editObjectData(setChillWaterData, field, value);

  const tonsCo2e = (chillWaterKwh / 1000) * tonsCo2ePerMwh;

  const saveEnabled = isEmissionsInputSchemaEnabled(
    chillWaterData,
    inputSchema
  );

  const onTransactionSave = () =>
    onSave({
      tonsCo2e,
      emissionData: chillWaterData,
    });

  return {
    inputBlock: (
      <ChillWaterInput
        editChillWaterData={editChillWaterData}
        {...chillWaterData}
        disabled={status === "locked"}
      />
    ),
    saveEnabled,
    onTransactionSave,
    tonsCo2e,
  };
};
export default useChillWaterInput;
